import { useState } from "react";
import { Box } from "@mui/material"
import { Trans, useTranslation } from "react-i18next"
import ProfileHeader from "../../../../components/ProfileHeader"
import IconButton from '@mui/material/IconButton';
import { Form, Formik } from "formik";
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import PhotoCameraIcon from '@mui/icons-material/PhotoCameraOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import OutlinedTextField from "../../../../components/formFields/OutlinedTextField";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useEffect } from "react";
import ticketInitialValues from "./ticketInitialValues";
import ticketValidationSchema from "./ticketValidationSchema";
import Image4 from "../../../../components/images/Image4";
import ItemsApi from "../../../../api/ItemsApi";
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import { ToastType } from "../../../../components/toast/ToastType";
import { useToastContext } from "../../../../context/ToastContext";
import {ROLE_BASE} from "../../../../utils/generalConstants";

const TicketFormThumbnail = (props: { files: File[], placeholder?: string, handleDelete: any }) => {

  const isImageFile = (file: File): boolean => {
    return /^image\/.*$/.test(file.type);
  }

  return (
    <Box className="ticket-form-file-thumbnails">
      {props.files.map((file: File, index: number) =>
        <Box className="ticket-form-file-thumbnail" key={index}>
          { (!!props.placeholder || !isImageFile(file)) &&
            <Box className="ticket-form-file-placeholder">
              <Trans i18nKey={props.placeholder || file.name} />
            </Box>
          }
          { isImageFile(file) ? <img src={URL.createObjectURL(file)} className="ticket-form-thumbnail-img"/> : <Image4/> }
          <IconButton size="small" className="ticket-form-file-delete-icon" onClick={() => props.handleDelete(index)}>
            <DeleteIcon fontSize="inherit" />
          </IconButton>
        </Box>
      )}
    </Box>
  )
}

type Props = {
  companyName: string,
  ticketId: string,
  plateNumber: string,
  handleSubmit: any,
  handleGoBack: any,
  userRole: any
}

export default (props: Props) => {
  const [, , showToast] = useToastContext()
  const [domainAttach, setDomainAttach] = useState<File | null>(null)
  const { t } = useTranslation();
  const [kmAttach, setKmAttach] = useState<File | null>(null)
  const [attached, setAttached] = useState<File[]>([])
  const [domainId, setDomainId] = useState('')
  const [kmId, setKmId] = useState('')
  const [attachedIds, setAttachedIds] = useState<string[]>([])
  const [showBackdrop, setShowBackDrop] = useState(false)
  useEffect(() => {
    if (!document.body.classList.contains("grey-background")) {
      document.body.classList.add("grey-background");
    }
  }, []);

  function saveItems(files: any, classification: string) {
    if (!files.length) return
    setShowBackDrop(true)

    const calls = Array.from(files).map((file: any) => ItemsApi.post(props.ticketId, file, classification))
    return Promise.all(calls)
    .then((result) => {
      setShowBackDrop(false)
      return Promise.resolve(result)
    })
  }

  function deleteItem(id: string): Promise<any> {
    setShowBackDrop(true)
    return ItemsApi.delete(id)
    .then(() => {
      setShowBackDrop(false)
      return Promise.resolve()
    })
  }

  function handledDomainAttach(files: any) {
    saveItems(files, 'DOMINIO')?.then((result) => {
      setDomainAttach(files[0])
      setDomainId(result[0].data.id)
    })
  }

  function deleteDomainAttach() {
    deleteItem(domainId)
    .then(() => {
      setDomainAttach(null)
      setDomainId('')
    })
  }

  function handledKmAttach(files: any) {
    saveItems(files, 'KILOMETROS')?.then((result) => {
      setKmAttach(files[0])
      setKmId(result[0].data.id)
    })
  }

  function deleteKmAttach() {
    deleteItem(kmId)
    .then(() => {
      setKmAttach(null)
      setKmId('')
    })
  }

  function handledAttached(files: File[]) {
    if ([...attached, ...files].length > 5) {
      showToast(ToastType.Error, t('screens.createMaintenance.maxImagesToastTitle'), t("screens.createMaintenance.maxImagesToastDesc"))
      return
    }
    const currentFileName = attached.map((fileAttached: any) => fileAttached.name)
    const newFiles = Array.from(files).filter((file: any) => !currentFileName.includes(file.name))
    saveItems(newFiles, '')?.then((result) => {
      setAttached([...attached, ...newFiles])
      setAttachedIds([...attachedIds, ...result.map(newFile => newFile.data.id)])
    })
  }

  function deleteAttached(index: number) {
    deleteItem(attachedIds[index])
    .then(() => {
      const newArr = [...attached];
      const newIds = [...attachedIds];
      newArr.splice(index, 1);
      newIds.splice(index, 1);
      setAttached(newArr)
      setAttachedIds(newIds)
    })
  }

  function attach(
    event: any,
    handler: any
  ) {
    const files = event?.target?.files
    if (!files?.length) return
    handler(files)
  }

  return (
    <Box>
      <Formik
        initialValues={ticketInitialValues(props)}
        onSubmit={props.handleSubmit}
        validationSchema={ticketValidationSchema}
        isInitialValid={false}
      >
        {({ isSubmitting, isValid }) => (
          <Form id="login">
            <ProfileHeader/>
            <Box className="create-maintenance-title"><Trans i18nKey={`screens.createMaintenance.title`}/></Box>
            <Box className="create-maintenance-card">
              <Box className="create-maintenance-card-title">{props.plateNumber}</Box>
              <OutlinedTextField
                label={`screens.createMaintenance.company`}
                name="company"
                autoComplete="company"
                fullWidth={true}
                i18n
                requiredField
              />
              <OutlinedTextField
                label={`screens.createMaintenance.userID`}
                name="userID"
                autoComplete="userID"
                fullWidth={true}
                i18n
                requiredField
              />
              <OutlinedTextField
                label={`screens.createMaintenance.km`}
                name="mileage"
                autoComplete="mileage"
                fullWidth={true}
                type='number'
                i18n
                requiredField
              />
              <Box className="ticket-form-file-thumbnails">
                {kmAttach && <TicketFormThumbnail files={[kmAttach]} placeholder={`screens.createMaintenance.km`} handleDelete={deleteKmAttach} /> }
                {domainAttach && <TicketFormThumbnail files={[domainAttach]} placeholder={`screens.createMaintenance.domain`} handleDelete={deleteDomainAttach} /> }
              </Box>
              <Box className="ticket-form-file-inputs">
                {!kmAttach &&
                  <Button variant="contained" component="label" className="ticket-form-button" startIcon={<PhotoCameraIcon />}>
                    <Trans i18nKey={`screens.createMaintenance.km`} />
                    <input
                      hidden
                      accept="image/*, application/pdf, application/msword, application/vnd.ms-excel"
                      type="file"
                      onChange={(event) => attach(event, handledKmAttach)}
                    />
                  </Button>
                }
                {!domainAttach &&
                  <Button variant="contained" component="label" className="ticket-form-button" startIcon={<PhotoCameraIcon />}>
                    <Trans i18nKey={`screens.createMaintenance.domain`} />
                    <input
                      hidden
                      accept="image/*, application/pdf, application/msword, application/vnd.ms-excel"
                      type="file"
                      onChange={(event) => attach(event, handledDomainAttach)}
                    />
                  </Button>
                }
              </Box>
              <OutlinedTextField
                label={`screens.createMaintenance.titleField`}
                name="title"
                autoComplete="title"
                fullWidth={true}
                i18n
                requiredField
              />
              <OutlinedTextField
                label={`screens.createMaintenance.descriptionField`}
                name="description"
                autoComplete="description"
                rows={5}
                maxLength={2083}
                fullWidth={true}
                i18n
                requiredField
              />
              {!!attached.length && <TicketFormThumbnail files={attached} handleDelete={deleteAttached} /> }
              <Box className="ticket-form-file-inputs">
                <Button variant="contained" component="label" className="ticket-form-button" startIcon={<PhotoCameraIcon />}>
                  <Trans i18nKey={`screens.createMaintenance.attachFiles`} />
                  <input
                    hidden
                    accept="image/*, application/pdf, application/msword, application/vnd.ms-excel"
                    multiple
                    type="file"
                    onChange={(event) => attach(event, handledAttached)}
                  />
                </Button>
              </Box>
            </Box>
           
            <Box className="bottom-nav-padding" />
            <Box className="bottom-nav">
              <IconButton className="bottom-nav-text-icon" onClick={props.handleGoBack}>
                <ArrowBackIcon />
              </IconButton>
              <Box sx={{flexGrow: "1"}}/>
              <Fab className="bottom-nav-text-fab" variant="extended" disabled={!isValid || isSubmitting} type="submit">
                { 'Guardar'}
                <SaveIcon/>
              </Fab>
            </Box>
          </Form>
        )}
      </Formik>
      <Backdrop sx={{ color: '#fff' }} open={showBackdrop}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  )
}
